import React, { useEffect, useState } from 'react';
import { isNativeApp } from '../lib/native';
import { ANDROID_PACKAGE, ANDROID_PUBLISHED, ANDROID_STORE_URL, IOS_STORE_URL } from '../lib/appLinks';
import { LOGO, LOGO_SRCSET, DELIVERY, DELIVERY_SRCSET, DESCUENTOS, DESCUENTOS_SRCSET } from '../lib/imagenes';
import { abiertaComoApp, alCambiarInstalacion, instalarDirecto, puedeInstalarDirecto } from '../lib/instalarWeb';

// Al entrar a la web desde el celular: ofrecer instalar la app (o abrirla si ya
// está instalada) o seguir en la web. Si elige la web, no se vuelve a preguntar
// por una semana. Mientras la app no esté en la tienda, se ofrece instalar la web
// como app desde el navegador (ícono en el inicio, se abre sin barra del navegador).
// En /comercio se ofrece la app de comercios, "Cupido Delivery", y en /descuentos la de los locales,
// "Cupido Descuentos" (otro manifest, ver server/index.js).
const DISMISS_DAYS = 7;
const esDescuentos = () => window.location.pathname.startsWith('/descuentos');
const esComercio = () => window.location.pathname.startsWith('/comercio') || esDescuentos();
const dismissKey = () => (esDescuentos() ? 'cupido_app_prompt_descuentos' : esComercio() ? 'cupido_app_prompt_comercio' : 'cupido_app_prompt_web');

function mobileOs() {
  const ua = navigator.userAgent || '';
  if (/android/i.test(ua)) return 'android';
  if (/iphone|ipad|ipod/i.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) return 'ios';
  return null;
}

function dismissedRecently() {
  try {
    const t = Number(localStorage.getItem(dismissKey()));
    return t && Date.now() - t < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch { return false; }
}

// Abre la app en la misma pantalla; si no está instalada, va a la tienda.
function openApp(os) {
  const path = window.location.pathname + window.location.search;
  const appUrl = `cupidoconmate://open?path=${encodeURIComponent(path)}`;
  if (os === 'android') {
    window.location.href = `intent://open?path=${encodeURIComponent(path)}#Intent;scheme=cupidoconmate;package=${ANDROID_PACKAGE};S.browser_fallback_url=${encodeURIComponent(ANDROID_STORE_URL)};end`;
    return;
  }
  const t = setTimeout(() => { if (document.visibilityState === 'visible') window.location.href = IOS_STORE_URL; }, 1500);
  document.addEventListener('visibilitychange', () => clearTimeout(t), { once: true });
  window.location.href = appUrl;
}

// Pasos para instalarla a mano cuando el navegador no ofrece el botón
const PASOS = {
  android: 'Tocá el menú ⋮ del navegador (arriba a la derecha) y elegí "Instalar app" o "Agregar a la pantalla de inicio".',
  ios: 'Tocá el botón Compartir (el cuadrado con la flecha hacia arriba) y elegí "Agregar a inicio".',
};

export default function AppPrompt() {
  const [os, setOs] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [web, setWeb] = useState(false);     // ofrecer instalar la web como app
  const [directo, setDirecto] = useState(puedeInstalarDirecto());
  const [pasos, setPasos] = useState(false);

  useEffect(() => alCambiarInstalacion(() => setDirecto(puedeInstalarDirecto())), []);

  useEffect(() => {
    const preview = new URLSearchParams(window.location.search).has('probar_app');
    if (isNativeApp() || abiertaComoApp() || (dismissedRecently() && !preview)) return;
    const detected = mobileOs();
    if (!detected) return;
    // Las apps de comercios solo existen como web instalable
    if (esComercio()) { setWeb(true); setOs(detected); return; }
    const storeUrl = detected === 'android' ? ANDROID_PUBLISHED && ANDROID_STORE_URL : IOS_STORE_URL;
    if (!storeUrl) { setWeb(true); setOs(detected); return; }
    // Chrome en Android puede decirnos si la app ya está instalada
    if (navigator.getInstalledRelatedApps) {
      navigator.getInstalledRelatedApps()
        .then(apps => setInstalled(apps.some(a => a.id === ANDROID_PACKAGE)))
        .catch(() => {});
    }
    setOs(detected);
  }, []);

  if (!os) return null;

  const stayOnWeb = () => {
    try { localStorage.setItem(dismissKey(), String(Date.now())); } catch { /* nada */ }
    setOs(null);
  };

  const instalar = async () => {
    if (!web) return openApp(os);
    if (directo) { if (await instalarDirecto()) setOs(null); return; }
    setPasos(true);
  };

  const comercio = esComercio();
  const descuentos = esDescuentos();

  return (
    <div className="modal-overlay" onClick={stayOnWeb}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ textAlign: 'center' }}>
        {descuentos
          ? <img src={DESCUENTOS} srcSet={DESCUENTOS_SRCSET} sizes="110px" alt="" width="110" style={{ display: 'block', margin: '0 auto 12px', height: 'auto' }} />
          : comercio
          ? <img src={DELIVERY} srcSet={DELIVERY_SRCSET} sizes="110px" alt="" width="110" style={{ display: 'block', margin: '0 auto 12px', height: 'auto' }} />
          : <img src={LOGO} srcSet={LOGO_SRCSET} sizes="72px" alt="" width="72" height="72" style={{ display: 'block', margin: '0 auto 12px' }} />}
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 24, marginBottom: 8 }}>
          {installed ? 'Tenés la app instalada' : descuentos ? 'Cupido Descuentos en tu celular' : comercio ? 'Cupido Delivery en tu celular' : 'Cupido con Mate en tu celular'}
        </h2>
        <p style={{ color: 'var(--texto-suave)', marginBottom: 24 }}>
          {installed
            ? '¿Querés seguir en la app?'
            : descuentos
              ? 'Instalá la app de tu local y validá los códigos con un toque desde el ícono, como cualquier app. Es gratis y no ocupa casi lugar.'
              : comercio
              ? 'Instalá la app de tu comercio y mirá tus pedidos con un toque desde el ícono, como cualquier app. Es gratis y no ocupa casi lugar.'
              : web
              ? 'Instalala en tu celular y entrá con un toque desde el ícono, como cualquier app. Es gratis y no ocupa casi lugar.'
              : 'Bajate la app y recibí un aviso cada vez que te escriban o tengas una conexión nueva.'}
        </p>
        {pasos && (
          <p style={{ background: 'rgba(255,255,255,0.08)', borderRadius: 12, padding: 12, marginBottom: 16 }}>
            {PASOS[os]}
          </p>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <button className="btn btn-primary" style={{ justifyContent: 'center' }} onClick={instalar}>
            {installed ? 'Abrir la app' : 'Instalar la app'}
          </button>
          <button className="btn btn-ghost" style={{ justifyContent: 'center' }} onClick={stayOnWeb}>
            Seguir en la web
          </button>
        </div>
      </div>
    </div>
  );
}
